import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Subscription, catchError, merge, switchMap, timer } from 'rxjs';
import { FEATURE_FLAG_DEFAULTS } from '@app/generated/feature-flags.constants';
import { FeatureFlags } from '@app/generated/feature-flags.types';
import { AppConfigService } from '@core/services/config/app-config.service';
import { SignalRService } from '@core/services/signal-r/signal-r.service';
import { runtimeFeatureFlagsLoaded } from '@core/state/config/config.reducer';

export interface RuntimeFeatureFlagSnapshot {
  revision: number;
  values: Record<string, boolean>;
  flags: RuntimeFeatureFlagDescriptor[];
}

interface RuntimeFeatureFlagDescriptor {
  key: string;
  overrideValue: boolean | null;
  effectiveValue: boolean;
}

const FALLBACK_POLL_INTERVAL_MS = 5 * 60 * 1000;

let deployedFeatureFlagValues: Partial<FeatureFlags> | undefined;

export function applyRuntimeFeatureFlagSnapshot(
  snapshot: RuntimeFeatureFlagSnapshot
): Partial<FeatureFlags> {
  deployedFeatureFlagValues ??= Object.keys(FEATURE_FLAG_DEFAULTS).reduce(
    (result, key) => {
      const typedKey = key as keyof FeatureFlags;
      result[typedKey] = AppConfigService.staticConfig[typedKey];
      return result;
    },
    {} as Partial<FeatureFlags>
  );
  const values = Object.keys(FEATURE_FLAG_DEFAULTS).reduce((result, key) => {
    const typedKey = key as keyof FeatureFlags;
    const snapshotValue = snapshot.values[key];
    const value =
      typeof snapshotValue === 'boolean'
        ? snapshotValue
        : deployedFeatureFlagValues?.[typedKey];
    if (typeof value === 'boolean') {
      result[typedKey] = value;
    }
    return result;
  }, {} as Partial<FeatureFlags>);
  AppConfigService.staticConfig = {
    ...AppConfigService.staticConfig,
    ...values,
  };
  return values;
}

/**
 * Keeps the browser's full feature-flag snapshot authoritative. SignalR carries only a revision
 * hint; this service always re-fetches the complete snapshot, and the slow timer is only a
 * correctness fallback for missed/disconnected notifications.
 */
@Injectable({ providedIn: 'root' })
export class RuntimeFeatureFlagsService implements OnDestroy {
  private readonly _subscription: Subscription;
  private _lastAppliedRevision = -1;

  constructor(
    http: HttpClient,
    config: AppConfigService,
    private readonly _store: Store,
    signalR: SignalRService
  ) {
    this._subscription = merge(
      timer(0, FALLBACK_POLL_INTERVAL_MS),
      signalR.runtimeFeatureFlagsRevision$
    )
      .pipe(
        switchMap(() =>
          http
            .get<RuntimeFeatureFlagSnapshot>(
              `${config.config.apiUrl}/runtime-feature-flags`
            )
            .pipe(catchError(() => EMPTY))
        )
      )
      .subscribe((snapshot) => this.applySnapshot(snapshot));
  }

  applySnapshot(snapshot: RuntimeFeatureFlagSnapshot): boolean {
    if (snapshot.revision < this._lastAppliedRevision) {
      return false;
    }

    this._lastAppliedRevision = snapshot.revision;
    const values = applyRuntimeFeatureFlagSnapshot(snapshot);
    this._store.dispatch(runtimeFeatureFlagsLoaded({ values }));
    return true;
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
